// ==========================================================
// Pixel-Perfect Evaluation & Scoring Engine (Master Craftsman Evaluator)
// ==========================================================

export interface EvaluationResult {
  accuracyScore: number;       // 0 - 100
  finishScore: number;         // 0 - 100
  speedBonus: number;          // Points
  colorScore: number;          // 0 - 100 (Optional/Color Lab mode)
  totalScore: number;          // Aggregated points
  stars: number;               // 1, 2, or 3
  meisterTitle: string;        // 職人称号
  isPass: boolean;
}

export class MasterEvaluator {
  /**
   * Evaluate canvas pixels against target base
   */
  public static evaluate(
    baseCanvas: HTMLCanvasElement,
    paintCanvas: HTMLCanvasElement,
    elapsedSeconds: number,
    parTimeSeconds: number,
    colorScore: number = 100
  ): EvaluationResult {
    const width = paintCanvas.width;
    const height = paintCanvas.height;

    const baseCtx = baseCanvas.getContext('2d')!;
    const paintCtx = paintCanvas.getContext('2d')!;

    const baseData = baseCtx.getImageData(0, 0, width, height).data;
    const paintData = paintCtx.getImageData(0, 0, width, height).data;

    let targetTotalPixels = 0;
    let targetCoveredPixels = 0;
    let bleedPixels = 0;

    // Sample pixels at step intervals for ultra-fast 60fps calculation
    const step = 4; // 1 in 16 pixels sampled
    const alphas: number[] = [];

    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const idx = (y * width + x) * 4;
        const baseAlpha = baseData[idx + 3];
        const paintAlpha = paintData[idx + 3];

        const isBaseTarget = baseAlpha > 30;
        const isPainted = paintAlpha > 30;

        if (isBaseTarget) {
          targetTotalPixels++;
          if (isPainted) {
            targetCoveredPixels++;
            alphas.push(paintAlpha);
          }
        } else {
          if (isPainted) {
            bleedPixels++;
          }
        }
      }
    }

    if (targetTotalPixels === 0) {
      return {
        accuracyScore: 0,
        finishScore: 0,
        speedBonus: 0,
        colorScore: 0,
        totalScore: 0,
        stars: 0,
        meisterTitle: '未作業',
        isPass: false
      };
    }

    // 1. Accuracy Calculation (Coverage vs Bleed)
    const rawCoverage = (targetCoveredPixels / targetTotalPixels) * 100;
    const bleedPenalty = (bleedPixels / targetTotalPixels) * 75; // Bleed penalty factor
    const accuracyScore = Math.max(0, Math.min(100, Math.round(rawCoverage - bleedPenalty)));

    // 2. Finish & Beauty Calculation (Uniformity of paint density)
    let finishScore = 0;
    if (alphas.length > 0) {
      const avgAlpha = alphas.reduce((a, b) => a + b, 0) / alphas.length;
      const variance = alphas.reduce((sum, a) => sum + Math.pow(a - avgAlpha, 2), 0) / alphas.length;
      const stdDev = Math.sqrt(variance);

      // Low stdDev means consistent coat; high stdDev means blotchy/streaky
      const uniformity = Math.max(0, 100 - stdDev * 0.8);
      // Density factor (optimal coverage ~ 85%+ alpha)
      const densityScore = Math.min(100, (avgAlpha / 255) * 110);

      finishScore = Math.round(uniformity * 0.6 + densityScore * 0.4);
    }

    // 3. Speed Bonus
    const remainingTime = Math.max(0, parTimeSeconds - elapsedSeconds);
    const speedBonus = Math.round(remainingTime * 45);

    // 4. Total Combined Score
    const baseWeightScore = accuracyScore * 40 + finishScore * 35 + colorScore * 25;
    const totalScore = Math.round(baseWeightScore + speedBonus);

    // 5. Star Rating & Craftsman Title
    let stars = 1;
    let meisterTitle = '見習い職人';
    let isPass = accuracyScore >= 40;

    const weightedAvg = (accuracyScore + finishScore + colorScore) / 3;

    if (weightedAvg >= 92 && accuracyScore >= 88) {
      stars = 3;
      meisterTitle = '塗聖 (Paint Meister)';
    } else if (weightedAvg >= 80 && accuracyScore >= 75) {
      stars = 3;
      meisterTitle = '名工・免許皆伝';
    } else if (weightedAvg >= 68 && accuracyScore >= 60) {
      stars = 2;
      meisterTitle = '熟練職人';
    } else if (weightedAvg >= 50) {
      stars = 1;
      meisterTitle = '一人前';
    }

    return {
      accuracyScore,
      finishScore,
      speedBonus,
      colorScore,
      totalScore,
      stars,
      meisterTitle,
      isPass
    };
  }
}
